import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormControl, Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useGrid, useModal, useToast, useAuth, usePage, usePopup } from "@/comn/hooks"; // hook
import {
    URLS,
    APIS,
    SF_LPCO_FEE_MDFY_DTL_SRCH,
    SF_LPCO_FEE_MDFY_DTL,
    SG_BLL_ITM_LIST,
} from "@/tz/go/egaz/osci/lpco/fee/services/OsciLpcoFeeService";
import { APIS_COMN, SF_COMN_TANSAD_INFO } from "@/tz/go/egaz/osci/colt/comn/OsciColtComnService"; // 공통 서비스
import { colEnvs, colUtils } from "@/tz/go/egaz/osci/colt/comn";
import { SF_APRV_RQST } from "@/comn/comn/aprv/service/AprvService";
import { OgaAprvRqst } from "@/comn/comn/aprv/OgaAprvRqst";
import { calcLength } from "framer-motion";

/*
 * Fee Modification Request Detail
 * !== 수수료 변경 신청 상세 (OSCI_LPCO_FEE_MDFY_R 기준) ==!
 */
export const OSCI0205010Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "coCd" },
            { area: "comnCd", comnCd: "OSC0056" },
            { area: "comnCd", comnCd: "OSC0057" },
            { area: "comnCd", comnCd: "OSC0058" },
            { area: "comnCd", comnCd: "OSC0067" },
            { area: "comnCd", comnCd: "OSC0072" },
            { area: "comnCd", comnCd: "OSC0018" },
            { area: "comnCd", comnCd: "OSC0020" },
            { area: "comnCd", comnCd: "OSC0023" },
        ],
    });

    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0009", "auth_osci0021", "auth_osci0003"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const popup = usePopup(); // Popup Hook !== Popup Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { bllMgmtId } = useParams();
    const { aprvYn } = useParams();
    const getEffectiveBllMgmtId = () => form.bllUpdtDtl.getValue("bllMgmtId") || bllMgmtId;

    // 폼 처리
    const [isReffTansad, setIsReffTansad] = useState(false);

    const [vsdItms, setVsdItms] = useState(false);

    const [bllDtlList, setBllDtlList] = useState(comnUtils.getGridData([]));
    const [bllItmList, setBllItmList] = useState(comnUtils.getGridData([]));

    // 버튼 처리
    const [isVisibleBtn, setIsVisibleBtn] = useState(false);
    const [reusStatCd, setReusStatCd] = useState("R");
    const [isZeroFee, setZeroFee] = useState(false);

    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "OSC0096" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Fee Modification Request Detail Search !== 수수료 변경 신청 상세 조회 ==!
        bllUpdtDtlSrch: useForm({
            defaultSchema: SF_LPCO_FEE_MDFY_DTL_SRCH,
            defaultValues: {
                bllMgmtId: bllMgmtId,
            },
        }),

        // Fee Modification Request Detail  !== 수수료 변경 신청 상세 폼 ==!
        bllUpdtDtl: useForm({
            defaultSchema: SF_LPCO_FEE_MDFY_DTL,
        }),
        // Common TANSAD Info  !== 공통 TANSAD정보 폼 ==!
        comnTansadInfo: useForm({
            defaultSchema: SF_COMN_TANSAD_INFO,
        }),
    };

    const readyFunc = {
        // NOTE: 기존 REUS 기반 인터페이스 연계(ZanMalipo 전송/확인, /feeAdjust)는 이번 변경 범위에
        // 포함되지 않으므로, 아래 reusStatCd 기반 버튼 노출/상태 로직은 그대로 유지한다.
        // !== The ZanMalipo send/confirm buttons still drive off the legacy REUS status
        // (reusStatCd, still returned by /updtDtl via a read-only join) since interface
        // integration migration is deferred per user instruction. ==!
        readyBtn: (data: any) => {
            const zeroFee = data?.feeTzsAmt === 0;
            setZeroFee(zeroFee);

            setIsVisibleBtn(false);
            if (data?.mdfyStatCd && data.mdfyStatCd === "R") {
                setIsVisibleBtn(true);
            }
        },
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        bllItmList: useGrid({
            defaultSchema: SG_BLL_ITM_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Fee Modification Request Detail !== 수수료 변경 신청 상세 조회 ==!
        getbllUpdtDtl: useFetch({
            api: () => APIS.getBllUpdtDtl(form.bllUpdtDtlSrch.getValues()),
            enabled: !!bllMgmtId,
            onSuccess: (data) => {
                const bllReusData = data.bllUpdtDtl.content;

                if (!bllReusData?.bllMgmtId) {
                    form.bllUpdtDtl.reset();
                    form.comnTansadInfo.reset();
                    modal.openModal({
                        content: "msg.osc.00182", // Please check Bill No.
                    });
                    return;
                }

                // form 데이터 세팅
                form.bllUpdtDtl.setValues({
                    ...bllReusData,
                });
                // form 데이터 세팅
                form.bllUpdtDtlSrch.setValues({
                    ...bllReusData,
                });

                // 버튼 컨트롤
                readyFunc.readyBtn(data.bllUpdtDtl?.content);

                if (bllReusData?.reffKeyTpCd && bllReusData.reffKeyTpCd === "T") {
                    let reffKeyNo = bllReusData?.reffKeyNo1 + "-" + bllReusData?.reffKeyNo2;
                    if (bllReusData?.reffKeyNo3 != null) {
                        reffKeyNo = reffKeyNo + "-" + bllReusData?.reffKeyNo3;
                    }
                    if (bllReusData?.reffKeyNo4 != null) {
                        reffKeyNo = reffKeyNo + "-" + bllReusData?.reffKeyNo4;
                    }
                    // Reference Key
                    form.bllUpdtDtl.setValue("reffKey", reffKeyNo);

                    if (bllReusData?.reffKeyTpCd == "T") {
                        const tansadNo =
                            bllReusData?.reffKeyNo1 + "-" + bllReusData?.reffKeyNo2 + "-" + bllReusData?.reffKeyNo3;
                        fetch.getTansadDtl.fetch(tansadNo);
                        setIsReffTansad(true);
                    }
                } else {
                    let reffKeyNo = bllReusData?.reffKeyNo1;
                    form.bllUpdtDtl.setValue("reffKey", reffKeyNo);
                }

                if (data.bllUpdtDtl.content.bllItms) {
                    setVsdItms(true);
                    if (data.bllUpdtDtl.content.bllItms && data.bllUpdtDtl.content.bllItms.length > 0) {
                        setVsdItms(true);
                        const filteredList = data.bllUpdtDtl.content.bllItms.filter(
                            (item: any) => Number(item.bllItmTzsAmt) !== 0,
                        );
                        setBllItmList(comnUtils.getGridData(filteredList));
                    }
                }
            },
            onError: () => {},
            showToast: true,
        }),
        // Get TANSAD Detail !== TANSAD 상세 조회 ==!
        getTansadDtl: useFetch({
            api: (tansadNo) => APIS_COMN.getTansad(tansadNo),
            onSuccess: (data) => {
                const tansadData = data.tansadInfo.content;

                if (!tansadData?.tansadNo) {
                    form.bllUpdtDtl.reset();
                    form.comnTansadInfo.reset();

                    modal.openModal({
                        content: "msg.osc.00146", // Please check TANSAD No.
                    });
                    return;
                }

                const exppnImppnTinNm =
                    colUtils.setMask(tansadData.exppnImppnTin, "tin") + "(" + tansadData.exppnImppnNm + ")";
                const dcltTinNm = colUtils.setMask(tansadData.dcltTin, "tin") + "(" + tansadData.dcltNm + ")";

                // form 데이터 세팅
                form.comnTansadInfo.setValues({
                    ...tansadData,
                    exppnImppnTinNm: exppnImppnTinNm,
                    dcltTinNm: dcltTinNm,
                });
            },
            onError: () => {},
            showToast: true,
        }),
        // Delete Fee Modification Request !== 수수료 변경 신청 삭제 ==!
        deleteLpcoFeeMdfy: useFetch({
            api: (feeId, mdfyRqstSn) => APIS.deleteLpcoFeeMdfy(feeId, mdfyRqstSn),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006", // 성공적으로 처리 되었습니다.
                    onConfirm: () => {
                        navigate(`${URLS.osci0205009q}`);
                    },
                    onCancel: () => {
                        navigate(`${URLS.osci0205009q}`);
                    },
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    console.log(error.response.data);
                }
                modal.openModal({
                    content: "msg.osc.00015", // 처리중 문제가 발생하였습니다.
                });
            },
        }),
        // NOTE: 기존 REUS 기반 인터페이스 연계(/feeAdjust)는 이번 변경 범위 밖이므로 그대로 유지한다.
        adjustFee: useFetch({
            api: (data) => {
                return APIS.adjustFee(data);
            },
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006", //Processed successfully.
                    onClose: () => {
                        fetch.getbllUpdtDtl.fetch();
                    },
                });
            },
            onError: (error) => {
                // Business errors (e.g. a reuse request of the bill not finished yet) carry their own message
                // !== 업무 오류(고지서의 재사용 요청이 아직 끝나지 않음 등)는 서버 메시지를 보여 준다 ==!
                const errorMessage = error?.response?.data?.errorMessage;
                const serverMsg = typeof errorMessage === "string" ? errorMessage : errorMessage?.content;
                modal.openModal({
                    content: serverMsg || "msg.osc.00015", //An error occurred during processing.
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Go Update Form !== 수정 화면 이동 ==!
        goUpdateForm: () => {
            navigate(URLS.osci0205011s + "/UPDATE" + "/" + getEffectiveBllMgmtId());
        },
        // Delete Fee Modification Request !== 수수료 변경 신청 삭제 ==!
        deleteLpcoFeeMdfy: () => {
            form.bllUpdtDtl.handleSubmit(
                (data) => {
                    modal.openModal({
                        content: t("msg.osc.00038", { "0": "Reus" }), // Do you want to remove Reus?
                        onConfirm: () => {
                            fetch.deleteLpcoFeeMdfy.fetch(
                                form.bllUpdtDtl.getValue("feeId"),
                                form.bllUpdtDtl.getValue("mdfyRqstSn"),
                            );
                        },
                    });
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // 데이터가 유효하지 않습니다.
                },
            )();
        },
        feeUpdate: form.bllUpdtDtl.handleSubmit(
            () => {
                const data = { ...form.bllUpdtDtl.getValues() };
                modal.openModal({
                    content: "msg.osc.00180", //Do you want to execute?
                    onConfirm: () => {
                        fetch.adjustFee.fetch(data);
                    },
                });
            },
            () => {
                toast.showToast({ type: "warning", content: "msg.osc.00003" });
            },
        ),
        feeConfirm: form.bllUpdtDtl.handleSubmit(
            () => {
                const data = { ...form.bllUpdtDtl.getValues() };
                modal.openModal({
                    content: "msg.osc.00287", //If the fee amount is 0, the bill will not be issued. Do you want to continue?
                    onConfirm: () => {
                        fetch.adjustFee.fetch(data);
                    },
                });
            },
            () => {
                toast.showToast({ type: "warning", content: "msg.osc.00003" });
            },
        ),
    };

    const render = {
        grid_bllDtlList: {
            head: {},
            cell: {},
            edit: {},
        },
        grid_bllItmList: {
            head: {},
            cell: {},
            edit: {
                bllItmAmt: (props: any) => {
                    const { binding, rowValues, value } = props;

                    return <span>{`${rowValues.bllItmAmt}`}</span>;
                },
            },
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        form.bllUpdtDtlSrch.setEditable(false);
        form.bllUpdtDtl.setEditable(false);
        form.comnTansadInfo.setEditable(false);
    }, []);

    return (
        <Page>
            {/* 수수료 변경 신청 상세 조회 */}
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtlSrch.schema.bllNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Label {...form.bllUpdtDtlSrch.schema.bllMgmtId}></Group.Label>
                            <Group.Col>
                                <Group.Control {...form.bllUpdtDtlSrch.schema.bllTpCd} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllUpdtDtlSrch.schema.bllSysmCd} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllUpdtDtlSrch.schema.bllYy} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllUpdtDtlSrch.schema.bllSrno} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.bllUpdtDtlSrch.schema.bllDgr} />
                            </Group.Col>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            {isReffTansad && (
                <>
                    {/* TANSAD */}
                    <Group>
                        <Group.Body>
                            <Group.Title title={"T_TANSAD_GNRL"} titleSize={2}></Group.Title>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.tansadNo} />
                                    <Group.Control {...form.comnTansadInfo.schema.tansadDt} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.regm} />
                                    <Group.Control {...form.comnTansadInfo.schema.csclPlnCd} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.exppnImppnTinNm} />
                                    <Group.Control {...form.comnTansadInfo.schema.dcltTinNm} />
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.comnTansadInfo.schema.dclrPrcssSttsCd} />
                                </Group.Row>
                            </Group.Section>
                        </Group.Body>
                    </Group>
                </>
            )}
            {/* Bill */}
            <Group>
                <Group.Body>
                    <Group.Title title={"L_BLL_GNRL"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtl.schema.bllNo} />
                            <Group.Control {...form.bllUpdtDtl.schema.bllDt} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtl.schema.ctrlNo} />
                            <Group.Control {...form.bllUpdtDtl.schema.zmpDt} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtl.schema.currCd} />
                            <Group.Control {...form.bllUpdtDtl.schema.bllStatCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtl.schema.feeAmt} />
                            <Group.Control {...form.bllUpdtDtl.schema.feeTzsAmt} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtl.schema.pyerMgmtNo} />
                            <Group.Control {...form.bllUpdtDtl.schema.payDdlnDt} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Col label="L_REFF_KEY">
                                {form.bllUpdtDtl.getValue("reffKey") && (
                                    <>
                                        <Group.Control {...form.bllUpdtDtl.schema.reffKeyTpCd} />
                                        <Group.Any> : </Group.Any>
                                        <Group.Control {...form.bllUpdtDtl.schema.reffKey} />
                                    </>
                                )}
                            </Group.Col>
                            <Group.Control {...form.bllUpdtDtl.schema.bllRgsrId} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            {vsdItms && (
                <Group>
                    <Group.Body>
                        {" "}
                        <Layout direction="row">
                            <Layout.Left>
                                <Group.Title title={"T_ITM_LST"} titleSize={2}></Group.Title>
                            </Layout.Left>
                        </Layout>
                        <Grid {...grid.bllItmList.grid} data={bllItmList} render={render.grid_bllItmList} />
                    </Group.Body>
                </Group>
            )}
            {/* 수수료 변경 신청 */}
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtl.schema.mdfyRmrk} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bllUpdtDtl.schema.mdfyStatCd} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            {aprvYn != "Y" && (
                <Layout direction="row">
                    <Layout.Left>
                        <Button
                            role="list"
                            onClick={() => {
                                navigate(URLS.osci0205009q);
                            }}
                        ></Button>
                    </Layout.Left>
                    {isVisibleBtn && (
                        <Layout.Right>
                            {!isZeroFee && <Button onClick={handler.feeUpdate}>{t("B_RQST")}</Button>}
                            {isZeroFee && <Button role="confirm" onClick={handler.feeConfirm}></Button>}
                            <Button
                                onClick={() => {
                                    handler.goUpdateForm();
                                }}
                            >
                                {t("B_UPDT")}
                            </Button>
                            <Button
                                role="delete"
                                onClick={() => {
                                    handler.deleteLpcoFeeMdfy();
                                }}
                            ></Button>
                            {/* <Button variant="primary" onClick={handler.onAprvRqstBtnClick}>
                                {t("B_APRV_RQST")}
                            </Button> */}
                        </Layout.Right>
                    )}
                </Layout>
            )}
        </Page>
    );
};
